import { useState } from 'react';
import { Button, Group, Paper, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useMutation } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { UpdateSystemOrganizationMutation } from './graphql';
import type { SystemOrganization } from './graphql';

function OrganizationDetailsFields({ organization }: { organization: SystemOrganization }) {
  const [name, setName] = useState(organization.name);
  const [slug, setSlug] = useState(organization.slug);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [, runUpdateOrganization] = useMutation(UpdateSystemOrganizationMutation);

  const save = async () => {
    setError(null);
    setIsSaving(true);

    const result = await runUpdateOrganization({
      id: organization.id,
      name: name.trim(),
      slug: slug.trim(),
      updatedAt: new Date().toISOString(),
    });

    setIsSaving(false);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to save organization'));
  };

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <ErrorAlert message={error} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.currentTarget.value)}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          />
        </SimpleGrid>
        <Text size="xs" c="dimmed">
          Created {new Date(organization.createdAt).toLocaleDateString()}
        </Text>
        <Group justify="flex-end">
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={() => void save()}
            loading={isSaving}
            disabled={!name.trim() || !slug.trim()}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

export function OrganizationDetailsForm({ organization }: { organization: SystemOrganization }) {
  return <OrganizationDetailsFields key={`${organization.name} ${organization.slug}`} organization={organization} />;
}
